import { useState } from 'react';
import { createGlobalModal } from '@hotjar/webapp/features/GlobalModal/createGlobalModal';
import type { GlobalModalProps } from '@hotjar/webapp/features/GlobalModal/createGlobalModal';
import { useAccountExperiment } from '@hotjar/webapp/hooks/useExperiment';
import type { VARIANTS } from '@hotjar/experimentation';
import { useOwnedAccount } from '@hotjar/account';
import { useAssignCampaignToAccount } from '@hotjar/webapp/hooks/useCampaigns';
import { useNotify } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { useEffectOnceWithDependencies } from '@hotjar/webapp/hooks/useEffectOnceWithDependencies';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import type { OwnedAccount } from '@hotjar/account';
import { ChurnHedgingModal } from './ChurnHedgingModal';

const variantMonthsMap: Record<VARIANTS, number> = {
  control: 1,
  variant1: 2,
  variant2: 3,
};

// campaign ids mapped to the number of months that they grant
// -> https://gitlab.com/hotjar/shared/insights/-/blob/309515effbd42f5701c7c0f86c3757d7be3803e8/src/insights/settings_core.py#L204-219
const campaignsMap: Record<number, string> = {
  1: '40b0ef6d7e60400bb18e81f3213859fa',
  2: '7d250ae25a564558a7708e81535e08ae',
  3: '2ad96fbf70e54712bcd787873e3e03db',
} as const;

export const churnHeadingsReasonsMap = {
  'Budget cuts': 'budget-cuts',
  'The project is finished': 'project-finished',
  'Temporary pause': 'temporary-pause',
} as const;

interface Props {
  downgradeReason: keyof typeof churnHeadingsReasonsMap;
}

type ErrorValues = 'close' | 'continue';

export function ChurnHedgingModalExperiment({
  ...props
}: Props & GlobalModalProps<number, ErrorValues>): JSX.Element | null {
  const { data: account } = useOwnedAccount();
  const { variant, inExperiment } = useAccountExperiment('pay_churn_hedging', account);
  const assignCampaignMutation = useAssignCampaignToAccount();
  const [state, setState] = useState<'default' | 'loading' | 'error'>('default');
  const { t } = useTranslationWithPrefix(`billing:churn-hedging`);
  const { showToast } = useNotify();
  const { loadedModal, closedModal, click, getGroupDataForAccount } = useTrack();

  const freeMonths = variantMonthsMap[variant];
  const reason = churnHeadingsReasonsMap[props.downgradeReason];
  const modalName = `Subscription extension offer ${freeMonths} months`;

  useEffectOnceWithDependencies(
    () => {
      loadedModal(modalName, getGroupDataForAccount(account as OwnedAccount), {
        modal_version: props.downgradeReason,
      });
    },
    [account, freeMonths],
    () => account && freeMonths && inExperiment,
  );

  if (freeMonths === undefined) {
    return null;
  }

  function trackCloseModal() {
    if (account) {
      closedModal(modalName, getGroupDataForAccount(account), {
        modal_version: props.downgradeReason,
      });
    }
  }

  function trackClick(element: string) {
    if (account) {
      click(element, modalName, getGroupDataForAccount(account));
    }
  }

  function onCancel(): void {
    props.onCancel('close');
    trackCloseModal();
  }

  function onContinue(): void {
    props.onCancel('continue');
    trackClick('Continue to downgrade');
  }

  async function onSubmit(): Promise<void> {
    if (account) {
      try {
        setState('loading');
        trackClick('Accept Subscription extension');
        await assignCampaignMutation.mutateAsync({
          campaignId: campaignsMap[freeMonths],
          accountId: String(account?.id),
        });
        window.location.reload();
        props.onSubmit();
      } catch (error) {
        showToast({
          text: t('offer-alredy-used'),
          kind: 'danger',
        });
      } finally {
        setState('default');
      }
    }
  }

  if (freeMonths === undefined || !account) {
    return null;
  }

  return (
    <ChurnHedgingModal
      freeMonths={freeMonths}
      reason={reason}
      onCancel={onCancel}
      onContinue={onContinue}
      onSubmit={onSubmit}
      isLoading={state === 'loading'}
    />
  );
}

export const openChurnHedgingModal = createGlobalModal<Props, number, ErrorValues>(
  ChurnHedgingModalExperiment,
);
